import { format, isValid, parseISO } from 'date-fns';
import { idType, selectItemType } from '@src/types';
import {
  getFilterSelectData,
  OperatorInfo,
} from '@uikit/Tables/Filtration/constants';
import {
  allStatuses,
  getOnTimeStatusStyle,
  statusGroupColors as helperStatusGroupColors,
  stGroupColors,
} from '@utils/helpers';
import React from 'react';
import { formatPhoneNumber } from '@src/utils';
import { FORMATTERS } from '@utils/formatters';
import { formatPercent } from '@component/LightVersion/Reports/ReportsComponents/line';

const costFields = [
  'cost',
  'averageRideCost',
  'totalRideCost',
  'totalRideshareCost',
  'totalCost',
  'costPerRide',
];

export const formatDateFilterValue = (value: string | Date): string => {
  if (value instanceof Date) {
    return isValid(value) ? format(value, 'MM/dd/yyyy') : '';
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) return '';

  const isoDateMatches = trimmedValue.match(/\d{4}-\d{2}-\d{2}/g);

  if (isoDateMatches?.length) {
    const formattedMatches = [...new Set(isoDateMatches)].map((dateStr) => {
      const parsed = parseISO(dateStr);
      return isValid(parsed) ? format(parsed, 'MM/dd/yyyy') : dateStr;
    });

    return formattedMatches.join(' - ');
  }

  const slashDateMatch = trimmedValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}/);
  if (slashDateMatch) {
    return slashDateMatch[0];
  }

  const parsedDate = new Date(trimmedValue);
  if (!isValid(parsedDate)) return trimmedValue;

  return format(parsedDate, 'MM/dd/yyyy');
};

export const isFilledDateValue = (value: unknown): boolean => {
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return Boolean(value);
};

export const getCurrentFilterValue = (
  value: string | boolean | Array<idType>,
  selectName: string = '',
  operator: string = '',
  filterType: string = '',
  groupFilter: OperatorInfo | null = null,
  filterOptions: OperatorInfo['booleanOptions'] = []
): string => {
  const isDateFilter = filterType === 'date' || filterType === 'dateTime';

  if (
    typeof value === 'boolean' ||
    filterType.toLowerCase() === 'boolean' ||
    filterType.toLowerCase() === 'localselect'
  ) {
    const booleanOptions = filterOptions.length
      ? filterOptions
      : groupFilter?.booleanOptions?.length
        ? groupFilter.booleanOptions
        : [
            { id: 1, value: true, label: 'Active' },
            { id: 2, value: false, label: 'Inactive' },
          ];

    return (
      booleanOptions.find((option) => String(option.value) === String(value))
        ?.label || ''
    );
  }

  if (Array.isArray(value)) {
    if (operator === 'range') {
      if (isDateFilter) {
        return value.map((el) => formatDateFilterValue(String(el))).join(' - ');
      }

      return value.join('-');
    }
    const currentSelectData: Array<selectItemType> =
      getFilterSelectData(selectName) || [];
    const currentValues = value.map((el): string => {
      return currentSelectData.find((elem) => elem.id === el)?.label || '';
    });
    return currentValues.join(',');
  }

  const isTimeFormat =
    typeof value === 'string' && /^\d{1,2}:\d{2}:\d{2}$/.test(value);
  if (isTimeFormat) {
    return value.split(':').slice(0, 2).join(':');
  }

  if (isDateFilter) {
    return formatDateFilterValue(value);
  }

  return value;
};

export const formatDateWithCurrentTime = (
  dateStr: string | Date,
  boundary: 'start' | 'end' = 'start'
): string | null => {
  let year: number;
  let month: number;
  let day: number;

  if (dateStr instanceof Date) {
    if (isNaN(dateStr.getTime())) return null;
    year = dateStr.getFullYear();
    month = dateStr.getMonth() + 1;
    day = dateStr.getDate();
  } else if (dateStr.trim() !== '') {
    const parts = dateStr.split('/');

    if (parts.length === 3) {
      [month, day, year] = parts.map(Number);

      if (isNaN(month) || isNaN(day) || isNaN(year)) return null;
    } else {
      const parsedDate = new Date(dateStr);
      if (isNaN(parsedDate.getTime())) return null;
      year = parsedDate.getFullYear();
      month = parsedDate.getMonth() + 1;
      day = parsedDate.getDate();
    }
  } else {
    return null;
  }

  const isEnd = boundary === 'end';
  const hours = isEnd ? 23 : 0;
  const minutes = isEnd ? 59 : 0;
  const seconds = isEnd ? 59 : 0;
  const milliseconds = isEnd ? 999 : 0;

  const finalDate = new Date(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds,
    milliseconds
  );

  return format(finalDate, "MM/dd/yyyy'T'HH:mm:ss.SSS");
};

type StatusConfig = (typeof allStatuses)[keyof typeof allStatuses] & {
  backgroundColor?: string;
};

const statusMap = {
  status: 'label',
  rideStatus: 'value',
  statusGroup: 'group',
};

const isTrue = (value: any) => {
  return value === 'true' || value === true;
};

const STATUS_LIST = Object.values(allStatuses) as StatusConfig[];

const normalizeStatusKey = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const resolveStatusConfig = (
  rawValue: unknown,
  columnKey: string
): StatusConfig | undefined => {
  if (rawValue == null) return undefined;

  if (typeof rawValue === 'number') {
    const byId = (allStatuses as any)[rawValue];
    if (byId) return byId;
  }

  const str = String(rawValue).trim();
  if (!str) return undefined;

  const asNumber = Number(str);
  if (!Number.isNaN(asNumber)) {
    const byId = (allStatuses as any)[asNumber];
    if (byId) return byId;
  }

  const lower = normalizeStatusKey(str);

  return STATUS_LIST.find((s) => {
    return normalizeStatusKey(s[statusMap[columnKey]]) === lower;
  });
};

const resolveStatusGroupFallbackConfig = (
  rawValue: unknown
): StatusConfig | undefined => {
  const normalized = normalizeStatusKey(rawValue);
  if (!normalized) return undefined;

  return STATUS_LIST.find((s) => {
    return (
      normalizeStatusKey(s.group) === normalized ||
      normalizeStatusKey(s.label) === normalized ||
      normalizeStatusKey(s.value) === normalized ||
      normalizeStatusKey(s.rideShare) === normalized
    );
  });
};

const resolveStatusGroupColorFromHelpers = (...values: unknown[]) => {
  for (const value of values) {
    const normalized = normalizeStatusKey(value);
    if (!normalized) continue;

    if (
      normalized === 'incomplete' ||
      normalized === 'expired' ||
      normalized === 'no show' ||
      normalized === 'member no show' ||
      normalized === 'driver no show' ||
      normalized === 'all incomplete statuses'
    ) {
      return helperStatusGroupColors.Incomplete;
    }
  }

  return undefined;
};

export const resolveMemberStatusBadgeStyle = (rawValue: unknown) => {
  const normalizedValue = normalizeStatusKey(rawValue);

  if (normalizedValue === 'active') {
    return {
      // backgroundColor: '#3EBB7AFF',
      backgroundColor: stGroupColors.complete,
      color: '#FFFFFFFF',
    };
  }

  if (normalizedValue === 'inactive') {
    return {
      // backgroundColor: '#57568EFF',
      backgroundColor: stGroupColors.incomplete,
      color: '#FFFFFFFF',
    };
  }

  return undefined;
};

const getStatusGroupTextColor = (backgroundColor?: string) => {
  const normalized = String(backgroundColor ?? '')
    .trim()
    .toUpperCase();

  if (normalized === '#BABABA' || normalized === '#BABABAFF') {
    return '#292850FF';
  }

  return '#FFFFFFFF';
};

const renderStatusBadgeConfig = (label, style) => {
  return {
    label,
    style,
    isStatusPill: true,
  };
};

export const renderCellContent = (row: any, col: any) => {
  // TODO need to create the same structure table for all tables!!!!! IMPORTANT ||| URGENT!!!!
  const colField = col.key ? col.key : col.field;
  const rawValue = row[colField];
  if (colField === 'onTimeStatus') {
    const style = getOnTimeStatusStyle(rawValue);
    if (rawValue == null || rawValue === '')
      return { label: 'No Time', style, isStatusPill: true };

    if (!style) {
      return { label: String(rawValue), style: {}, isStatusPill: false };
    }

    return renderStatusBadgeConfig(String(rawValue), style);
  }

  const isStatusColumn =
    col.type === 'status' ||
    colField === 'status' ||
    colField === 'rideStatus' ||
    colField === 'statusGroup' ||
    colField === 'onTimeStatus';

  if (isStatusColumn) {
    if (rawValue == null || rawValue === '')
      return { label: '-', style: {}, isStatusPill: false };

    const statusConfig = resolveStatusConfig(rawValue, colField);
    const statusGroupFallbackConfig =
      colField === 'statusGroup' && !statusConfig
        ? resolveStatusGroupFallbackConfig(rawValue)
        : undefined;
    const statusGroupReferenceConfig =
      colField === 'statusGroup'
        ? (statusConfig ?? statusGroupFallbackConfig)
        : undefined;
    const statusGroupConfigFromGroup =
      colField === 'statusGroup'
        ? STATUS_LIST.find(
            (s) =>
              normalizeStatusKey(s.group) ===
              normalizeStatusKey(statusGroupReferenceConfig?.group)
          )
        : undefined;
    const label =
      colField === 'statusGroup'
        ? (statusGroupReferenceConfig?.group ?? (rawValue as React.ReactNode))
        : (statusConfig?.[statusMap[colField]] ??
          (rawValue as React.ReactNode));
    const helperStatusGroupColor =
      colField === 'statusGroup'
        ? resolveStatusGroupColorFromHelpers(
            rawValue,
            label,
            statusGroupReferenceConfig?.group,
            statusGroupReferenceConfig?.value,
            statusGroupReferenceConfig?.label
          )
        : undefined;

    const memberStatusBadgeStyle =
      colField === 'status' || colField === 'contractorStatus'
        ? resolveMemberStatusBadgeStyle(rawValue)
        : undefined;

    if (!statusConfig && !statusGroupFallbackConfig) {
      if (memberStatusBadgeStyle) {
        return renderStatusBadgeConfig(rawValue, memberStatusBadgeStyle);
      }

      return { label: rawValue, style: {}, isStatusPill: false };
    }

    const backgroundColor =
      colField === 'statusGroup'
        ? (helperStatusGroupColor ??
          statusGroupConfigFromGroup?.stColor ??
          statusGroupReferenceConfig?.stColor ??
          statusGroupConfigFromGroup?.backgroundColor ??
          statusGroupReferenceConfig?.backgroundColor ??
          statusGroupConfigFromGroup?.backgroundColor ??
          statusGroupReferenceConfig?.backgroundColor ??
          'transparent')
        : (statusConfig?.backgroundColor ??
          statusConfig?.backgroundColor ??
          'transparent');

    const color =
      colField === 'statusGroup'
        ? getStatusGroupTextColor(backgroundColor)
        : (statusConfig?.color ?? 'inherit');

    return renderStatusBadgeConfig(label, { backgroundColor, color });
  }

  if (col.type === 'percent') {
    const num = Number(rawValue);
    return {
      label: formatPercent(num),
      style: {},
      isStatusPill: false,
    };
  }

  if (col.type === 'date') {
    if (!rawValue) return { label: '-', style: {}, isStatusPill: false };
    return {
      label: formatDateFilterValue(rawValue),
      style: {},
      isStatusPill: false,
    };
  }

  if (col.type === 'boolean') {
    if (rawValue == null || rawValue === '')
      return { label: '-', style: {}, isStatusPill: false };

    if (typeof rawValue === 'string') {
      const normalizedValue = rawValue.trim().toLowerCase();

      if (normalizedValue === 'true')
        return { label: 'Yes', style: {}, isStatusPill: false };
      if (normalizedValue === 'false')
        return { label: 'No', style: {}, isStatusPill: false };
    }

    return { label: rawValue ? 'Yes' : 'No', style: {}, isStatusPill: false };
  }

  if (col.type === 'number') {
    if (rawValue == null || rawValue === '')
      return { label: '0', style: {}, isStatusPill: false };

    const num = Number(rawValue);
    if (!Number.isFinite(num))
      return { label: '0', style: {}, isStatusPill: false };

    return { label: String(num), style: {}, isStatusPill: false };
  }

  if (
    (colField === 'pickupTime' || colField === 'scheduledPickupTime') &&
    isTrue(row.willCallIndicator) &&
    !row.pickupTime
  ) {
    return { label: 'WCALL', style: {}, isStatusPill: false };
  }
  // this is for checking all costs fields and convert them to "0" if not exist!!
  if (costFields.includes(colField)) {
    return {
      label: FORMATTERS.toDesiredCurrency({ value: row[colField] ?? 0 }),
      style: {},
      isStatusPill: false,
    };
  }

  if (colField === 'contactNumber') {
    return {
      label: formatPhoneNumber(row.contactNumber),
      style: {},
      isStatusPill: false,
    };
  }

  return { label: rawValue ?? '-', style: {}, isStatusPill: false };
};
